import { type ReactNode, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { type GuestInviteApplyStatus } from '@apm/contracts';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/states';
import { useAuth } from '../features/auth/auth-context';
import { GoogleSignIn } from '../features/auth/components/google-sign-in';
import { guestApi } from '../features/guests/api';
import { errorMessage } from '../lib/error-message';

/**
 * Trang của **link mời cộng tác viên ngoài** — APM-GUEST-g, docs/plan-guest-collaborator.md §4.7.
 * Khuôn `/client-invite/$token` (APM-CLINV-c).
 *
 * Nằm ngoài `_app`: người mở link thường **chưa có** tài khoản, và vỏ `_app` đá người
 * chưa đăng nhập về `/login`. Ba nhánh:
 *
 * - chưa đăng nhập ⇒ form tên / email / mật khẩu. Tạo xong là có phiên ngay, rồi báo
 *   "đang chờ duyệt".
 * - đã đăng nhập ⇒ một nút "Gửi yêu cầu tham gia".
 * - link hỏng (hết hạn, thu hồi) ⇒ nói ngay, không bày ra form sẽ hỏng.
 *
 * Gửi xong **không** có quyền gì ở dự án: PM phải duyệt (APM-GUEST-f).
 */
export const Route = createFileRoute('/guest-invite/$token')({
  component: GuestInvitePage,
});

function GuestInvitePage(): ReactNode {
  const { t } = useTranslation();
  const { token } = Route.useParams();
  const { status, user, logout } = useAuth();
  const [outcome, setOutcome] = useState<GuestInviteApplyStatus | null>(null);

  const preview = useQuery({
    queryKey: ['guest-invite', 'preview', token],
    queryFn: () => guestApi.preview(token),
    retry: false,
  });
  const apply = useMutation({
    mutationFn: () => guestApi.apply(token),
    onSuccess: (data) => setOutcome(data.status),
  });

  useEffect(() => {
    // Link này thường mở ở một tab lẫn giữa mười tab khác.
    document.title = t('guestInvite.title');
  }, [t]);

  let body: ReactNode;
  if (preview.isError) {
    body = (
      <>
        <CardHeader>
          <CardTitle>{t('guestInvite.invalidTitle')}</CardTitle>
          <CardDescription>{t('guestInvite.invalid')}</CardDescription>
        </CardHeader>
      </>
    );
  } else if (!preview.data || status === 'restoring') {
    body = (
      <CardContent className="pt-6">
        <Skeleton className="h-32" />
      </CardContent>
    );
  } else {
    const { projectName, projectKey, orgName } = preview.data;
    body = (
      <>
        <CardHeader>
          <CardTitle>{t('guestInvite.heading', { project: projectName })}</CardTitle>
          <CardDescription>
            {t('guestInvite.subheading', { org: orgName, key: projectKey })}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {outcome ? (
            <Outcome status={outcome} />
          ) : status === 'authenticated' && user ? (
            <div className="flex flex-col gap-3">
              <p className="text-sm">{t('guestInvite.signedInAs', { name: user.displayName })}</p>
              {apply.isError && (
                <p role="alert" className="text-sm text-rag-red">
                  {errorMessage(apply.error, t)}
                </p>
              )}
              <Button disabled={apply.isPending} onClick={() => apply.mutate()}>
                {t('guestInvite.apply')}
              </Button>
              <button
                type="button"
                className="self-start text-xs text-muted-foreground underline"
                onClick={() => void logout()}
              >
                {t('guestInvite.notYou')}
              </button>
            </div>
          ) : (
            /*
              Chỉ Google — người dùng chốt 2026-10-05 (APM-GAUTH-e), không còn form email
              + mật khẩu. Người đã có tài khoản quay lại đúng trang này rồi bấm "Gửi yêu
              cầu"; người mới đi qua `/onboarding?guestInvite=…`, nơi tài khoản ra đời
              cùng đơn xin vào.
            */
            <div className="flex flex-col gap-3">
              <p className="text-sm text-muted-foreground">{t('guestInvite.googleHelp')}</p>
              <GoogleSignIn next={`/guest-invite/${token}`} />
            </div>
          )}
        </CardContent>
      </>
    );
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-muted p-6">
      <Card className="w-full max-w-md">{body}</Card>
    </main>
  );
}

/** Kết cục sau khi gửi — mỗi trạng thái một câu, và một đường đi tiếp. */
function Outcome({ status }: { readonly status: GuestInviteApplyStatus }): ReactNode {
  const { t } = useTranslation();
  const toWork = status === 'ok' || status === 'pending' || status === 'already_member';

  return (
    <div role="status" className="flex flex-col gap-3">
      <p className="text-sm">{t(`guestInvite.outcome.${status}`)}</p>
      {/* `/` chứ không `/work`: vỏ app tự đưa cộng tác viên sang trang làm việc của họ. */}
      <Button asChild variant={toWork ? 'default' : 'outline'}>
        <Link to="/">{toWork ? t('guestInvite.toWork') : t('guestInvite.toHome')}</Link>
      </Button>
    </div>
  );
}
